/**
 * CircuitBreakerModal — Full-screen blocking overlay shown when the circuit
 * breaker trips. Polls /api/circuit-breaker/status every 10 seconds and also
 * reacts to SSE system events containing 'CIRCUIT BREAKER'.
 *
 * Role-based behaviour:
 *  • If cb_decision_permission matches user role → show decision buttons
 *  • Otherwise → show read-only "waiting for admin decision" view
 *
 * Auto-dismisses when adminDecision is 'continue' or 'stop_for_day'.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useSSE } from '../hooks/useSSE'
import { API_BASE } from '../utils/api'

// ─── Types ──────────────────────────────────────────────────────────────────

interface CBStatus {
  tripped: boolean
  adminDecision: 'pending' | 'continue' | 'stop_for_day' | null
  combinedPnl: number
  allottedMargin: number
  lossPercent: number
  threshold: number
  triggeredAt: string | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtINR(v: number): string {
  const abs = Math.abs(v)
  const s =
    abs >= 1e7 ? `₹${(abs / 1e7).toFixed(2)}Cr`
    : abs >= 1e5 ? `₹${(abs / 1e5).toFixed(2)}L`
    : `₹${abs.toLocaleString('en-IN')}`
  return v < 0 ? `-${s}` : `+${s}`
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    }) + ' IST'
  } catch {
    return iso
  }
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 18,
        height: 18,
        border: '2px solid rgba(148,163,184,0.3)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        animation: 'cbSpin 0.7s linear infinite',
        flexShrink: 0,
      }}
    />
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CircuitBreakerModal() {
  const { session, token } = useAuth()
  const { addToast } = useToast()
  const sse = useSSE()

  const [cbStatus, setCbStatus] = useState<CBStatus | null>(null)
  const [decisionPermission, setDecisionPermission] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [visible, setVisible] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const userRole = session?.user.role ?? null

  // ─── Fetch CB status ────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    if (!token) return
    try {
      const res = await fetch(`${API_BASE}/api/circuit-breaker/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data: CBStatus = await res.json()
      setCbStatus(data)

      // Fetch permission setting when tripped & pending
      if (data.tripped && data.adminDecision === 'pending' && decisionPermission === null) {
        const sRes = await fetch(`${API_BASE}/api/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (sRes.ok) {
          const settings = await sRes.json()
          setDecisionPermission(settings.cb_decision_permission ?? 'admin')
        }
      }

      // Show/hide modal
      if (data.tripped && data.adminDecision === 'pending') {
        setVisible(true)
      } else if (
        data.adminDecision === 'continue' ||
        data.adminDecision === 'stop_for_day'
      ) {
        setVisible(false)
      }
    } catch (err) {
      console.warn('[CB] Status fetch failed:', err)
    }
  }, [token, decisionPermission])

  // ─── Start polling ──────────────────────────────────────────────────────

  useEffect(() => {
    fetchStatus()
    pollRef.current = setInterval(fetchStatus, 10_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [fetchStatus])

  // ─── React to SSE system events ────────────────────────────────────────

  useEffect(() => {
    const ev = sse.lastEvent
    if (!ev || ev.type !== 'system') return
    const msg: string = ev.data?.message ?? ''
    if (msg.toUpperCase().includes('CIRCUIT BREAKER')) {
      fetchStatus()
    }
  }, [sse.lastEvent, fetchStatus])

  // ─── Decision handler ───────────────────────────────────────────────────

  const handleDecision = useCallback(
    async (decision: 'continue' | 'stop_for_day') => {
      if (!token || submitting) return
      setSubmitting(true)
      try {
        const res = await fetch(`${API_BASE}/api/circuit-breaker/decision`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ decision }),
        })
        if (res.ok) {
          addToast(
            decision === 'continue'
              ? '⚡ Continuing with new entries — CB reset.'
              : '🛑 Stopped for the day — all strategies paused.',
            decision === 'continue' ? 'info' : 'warning',
            8000,
          )
          setVisible(false)
          // Refresh status after decision
          setTimeout(fetchStatus, 500)
        } else {
          const err = await res.json().catch(() => ({ error: 'Failed' }))
          addToast(`CB decision failed: ${err.error ?? 'Unknown error'}`, 'error', 6000)
        }
      } catch (e) {
        addToast('CB decision request failed — check connection.', 'error', 6000)
      } finally {
        setSubmitting(false)
      }
    },
    [token, submitting, addToast, fetchStatus],
  )

  // ─── Don't render when not visible ────────────────────────────────────

  if (!visible || !cbStatus) return null

  const canDecide =
    decisionPermission === null
      ? false // still loading settings
      : decisionPermission === 'admin'
      ? userRole === 'admin'
      : decisionPermission === 'trader'
      ? userRole === 'trader' || userRole === 'admin'
      : true // 'all' → everyone

  const lossAmt = cbStatus.combinedPnl // already negative when a loss
  const thresholdPct = cbStatus.threshold ?? 3

  return (
    <>
      {/* Inline keyframe styles — avoids CSS file coupling */}
      <style>{`
        @keyframes cbSpin { to { transform: rotate(360deg); } }
        @keyframes cbPulseRing {
          0% { transform: scale(0.85); opacity: 1; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(0.85); opacity: 0; }
        }
        @keyframes cbSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes cbFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .cb-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0, 0, 0, 0.82);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: cbFadeIn 0.3s ease;
        }
        .cb-card {
          position: relative;
          width: 560px;
          max-width: 95vw;
          background: linear-gradient(
            145deg,
            rgba(18, 8, 8, 0.96) 0%,
            rgba(24, 10, 10, 0.94) 100%
          );
          border: 1.5px solid rgba(239, 68, 68, 0.35);
          border-radius: 1.25rem;
          box-shadow:
            0 0 0 1px rgba(239, 68, 68, 0.1),
            0 32px 80px -12px rgba(0, 0, 0, 0.7),
            0 0 60px -10px rgba(239, 68, 68, 0.2);
          overflow: hidden;
          animation: cbSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cb-card-readonly {
          background: linear-gradient(
            145deg,
            rgba(8, 14, 26, 0.97) 0%,
            rgba(13, 21, 40, 0.95) 100%
          );
          border-color: rgba(234, 179, 8, 0.35);
          box-shadow:
            0 0 0 1px rgba(234, 179, 8, 0.1),
            0 32px 80px -12px rgba(0, 0, 0, 0.7),
            0 0 60px -10px rgba(234, 179, 8, 0.15);
        }
        .cb-top-bar {
          height: 3px;
          background: linear-gradient(90deg, #ef4444, #dc2626, #ef4444);
          background-size: 200% 100%;
          animation: cbBarShift 2s linear infinite;
        }
        .cb-top-bar-yellow {
          background: linear-gradient(90deg, #eab308, #ca8a04, #eab308);
          background-size: 200% 100%;
          animation: cbBarShift 2s linear infinite;
        }
        @keyframes cbBarShift {
          from { background-position: 0 0; }
          to   { background-position: 200% 0; }
        }
      `}</style>

      <div className="cb-overlay">
        <div className={`cb-card ${!canDecide ? 'cb-card-readonly' : ''}`}>
          {/* Top accent bar */}
          <div className={`cb-top-bar ${!canDecide ? 'cb-top-bar-yellow' : ''}`} />

          {canDecide ? (
            // ── Decision view (admin / permitted user) ──────────────────
            <div style={{ padding: '2rem 2.5rem 2.5rem' }}>
              {/* Icon + title */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: 'rgba(239,68,68,0.15)',
                      border: '1.5px solid rgba(239,68,68,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.625rem',
                    }}
                  >
                    🚨
                  </div>
                  {/* Pulse ring */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      border: '2px solid rgba(239,68,68,0.5)',
                      animation: 'cbPulseRing 1.5s ease-out infinite',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'var(--red)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    System Alert
                  </div>
                  <h2
                    style={{
                      fontSize: '1.375rem',
                      fontWeight: 800,
                      color: '#fef2f2',
                      lineHeight: 1.2,
                    }}
                  >
                    Circuit Breaker Triggered
                  </h2>
                </div>
              </div>

              {/* Description */}
              <p
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem',
                  lineHeight: 1.65,
                }}
              >
                Combined loss has reached the{' '}
                <strong style={{ color: 'var(--red)' }}>{thresholdPct}% threshold</strong>.
                All strategies have been automatically paused.
              </p>

              {/* Metrics grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.625rem',
                  marginBottom: '1.75rem',
                }}
              >
                {[
                  {
                    label: 'Combined P&L',
                    value: fmtINR(lossAmt),
                    accent: lossAmt < 0 ? 'var(--red)' : 'var(--green)',
                    large: true,
                  },
                  {
                    label: 'Allotted Margin',
                    value: fmtINR(cbStatus.allottedMargin),
                    accent: 'var(--text-primary)',
                    large: false,
                  },
                  {
                    label: 'Loss %',
                    value: `${Math.abs(cbStatus.lossPercent ?? 0).toFixed(2)}%`,
                    accent: 'var(--red)',
                    large: false,
                  },
                  {
                    label: 'Triggered At',
                    value: fmtTime(cbStatus.triggeredAt),
                    accent: 'var(--text-secondary)',
                    large: false,
                  },
                ].map(({ label, value, accent, large }) => (
                  <div
                    key={label}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(239,68,68,0.15)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '0.75rem 1rem',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.5625rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--text-muted)',
                        marginBottom: '0.375rem',
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: large ? '1.25rem' : '0.9375rem',
                        fontWeight: 800,
                        color: accent,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div
                style={{
                  borderTop: '1px solid rgba(239,68,68,0.15)',
                  marginBottom: '1.25rem',
                }}
              />

              <p
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: '1rem',
                }}
              >
                What would you like to do?
              </p>

              {/* Decision buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn"
                  disabled={submitting}
                  onClick={() => handleDecision('continue')}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    padding: '0.75rem 1rem',
                    background: 'rgba(56, 189, 248, 0.08)',
                    border: '1.5px solid rgba(56, 189, 248, 0.35)',
                    color: 'var(--accent)',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    opacity: submitting ? 0.5 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    gap: '0.5rem',
                  }}
                >
                  {submitting ? <Spinner /> : '⚡'}
                  Continue with New Entries
                </button>
                <button
                  className="btn"
                  disabled={submitting}
                  onClick={() => handleDecision('stop_for_day')}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    padding: '0.75rem 1rem',
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1.5px solid rgba(239, 68, 68, 0.45)',
                    color: 'var(--red)',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    opacity: submitting ? 0.5 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    gap: '0.5rem',
                  }}
                >
                  {submitting ? <Spinner /> : '🛑'}
                  Stop for Day
                </button>
              </div>

              {/* Warning note */}
              <p
                style={{
                  fontSize: '0.6875rem',
                  color: 'var(--text-muted)',
                  marginTop: '1rem',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Choosing "Stop for Day" will keep all positions paused until next session.
                "Continue" resets the breaker and allows new entries.
              </p>
            </div>
          ) : (
            // ── Read-only view (trader waiting for admin) ───────────────
            <div
              style={{
                padding: '2.5rem 2.5rem 2.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '1.25rem',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(234,179,8,0.12)',
                  border: '1.5px solid rgba(234,179,8,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                }}
              >
                ⏸
              </div>

              <div>
                <div
                  style={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'var(--yellow)',
                    marginBottom: '0.5rem',
                  }}
                >
                  Trading Paused
                </div>
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    color: '#fefce8',
                    marginBottom: '0.5rem',
                  }}
                >
                  Circuit Breaker Triggered
                </h2>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6,
                    maxWidth: 340,
                  }}
                >
                  The combined loss threshold has been reached. All strategies have
                  been paused. Waiting for admin decision before trading can resume.
                </p>
              </div>

              {/* Metrics summary (compact) */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {[
                  { label: 'P&L', value: fmtINR(lossAmt), accent: 'var(--red)' },
                  { label: 'Loss %', value: `${Math.abs(cbStatus.lossPercent ?? 0).toFixed(2)}%`, accent: 'var(--red)' },
                  { label: 'At', value: fmtTime(cbStatus.triggeredAt), accent: 'var(--text-secondary)' },
                ].map(({ label, value, accent }) => (
                  <div
                    key={label}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(234,179,8,0.15)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '0.5rem 0.875rem',
                      minWidth: 80,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.5rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--text-muted)',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.875rem',
                        fontWeight: 800,
                        color: accent,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Spinner + message */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  fontSize: '0.8125rem',
                  color: 'var(--text-muted)',
                }}
              >
                <Spinner />
                Waiting for admin decision…
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
